import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ContentService } from 'src/app/views/content/content.service';
import { MaterialModule } from '../../material.module';

export interface LectureDialogData {
  mode: 'create' | 'edit';
  courseID: string;
  sectionID: string;
  lectureID?: string;
  lectureName?: string;
}

@Component({
  selector: 'app-lecture-dialog',
  templateUrl: './lecture-dialog.component.html',
  styleUrls: ['./lecture-dialog.component.scss'],
  standalone: true,
  imports: [MaterialModule, TranslateModule, FormsModule],
})
export class LectureDialogComponent implements OnInit {
  loading = false;
  buttonLock = false;
  lectureName = '';
  selectedLanguage = this.translate.currentLang || 'fr';
  course: any;
  lecture: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LectureDialogData,
    public dialogRef: MatDialogRef<LectureDialogComponent>,
    private toast: ToastService,
    private errorHandler: ErrorHandlerService,
    private translate: TranslateService,
    private contentService: ContentService
  ) {}

  ngOnInit() {
    if (this.data.mode === 'edit') {
      this.initEditMode();
    } else {
      this.lectureName = this.data.lectureName || '';
    }
  }

  initEditMode() {
    this.loading = true;
    this.contentService.getTheCourse(this.data.courseID).subscribe({
      next: (course) => {
        this.loading = false;
        this.course = course;

        if (this.data.sectionID && this.data.lectureID && this.course.sections) {
          const section = this.course.sections.find((s: any) => s._id === this.data.sectionID);
          if (section && section.lectures) {
            const foundLecture = section.lectures.find((l: any) => l._id === this.data.lectureID);
            if (foundLecture) {
              this.lecture = foundLecture;
              let actualName = foundLecture.name;
              if (foundLecture.__name) {
                const edition = foundLecture.__name.editions.find((e: any) => e.language === this.selectedLanguage);
                if (edition) {
                  actualName = edition.content;
                }
              }
              this.lectureName = actualName;
            }
          }
        }
      },
      error: (err) => {
        this.loading = false;
        this.toast.snackbar.open(this.translate.instant('Snackbar.CreatingCourseFail'), 'Ok', { duration: 2000 });
      },
    });
  }

  onSubmit() {
    if (!this.lectureName.trim()) return;

    if (this.data.mode === 'create') {
      this.createLecture();
    } else if (this.data.mode === 'edit') {
      this.editLecture();
    }
  }

  createLecture() {
    this.buttonLock = true;
    this.contentService
      .courseAddLecture({
        _id: this.data.courseID,
        sectionID: this.data.sectionID,
        lectureName: this.lectureName,
        language: this.selectedLanguage,
      })
      .subscribe({
        next: (res) => {
          this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 2000 });
          this.dialogRef.close('ok');
        },
        error: (err) => {
          this.buttonLock = false;
          if (err.error?.err === 'not-allowed-to-create-content') {
            this.toast.snackbar.open(this.translate.instant('Snackbar.NotAllowedToCreateContent'), 'OK');
          } else {
            this.toast.snackbar.open(this.translate.instant('Snackbar.CreatingLectureFail'), 'Ok', { duration: 4000 });
          }
        },
      });
  }

  editLecture() {
    if (!this.lecture) {
      this.dialogRef.close();
      return;
    }

    this.buttonLock = true;
    this.lecture.name = this.lectureName;
    this.contentService
      .editLecture({
        courseID: this.course._id,
        sectionID: this.data.sectionID,
        lecture: this.lecture,
        language: this.selectedLanguage,
        name: this.lectureName,
      })
      .subscribe({
        next: (res) => {
          this.toast.snackbar.open(this.translate.instant('Snackbar.Done'), 'Ok', { duration: 2000 });
          this.dialogRef.close('ok');
        },
        error: (err) => {
          this.buttonLock = false;
          if (!this.errorHandler.handle401Error(err)) {
            this.toast.snackbar.open(this.translate.instant('Snackbar.EditingLectureFail'), 'Ok', { duration: 2000 });
          }
        },
      });
  }
}
